// Poll fallback для pending rich_card (plan / material / essay): если Socket.IO
// *:ready пропущен, карточка не должна залипать до F5 / PowerSync.

import { trpcQuery } from "@/lib/trpc-fetch";
import { applyCardLivePatch } from "./card-live";

export type RichCardEntityKind = "plan" | "material" | "essay";

export interface PendingRichCardEntity {
	kind: RichCardEntityKind;
	id: string;
}

const pendingById = new Map<string, PendingRichCardEntity>();

const ENTITY_KINDS = new Set<RichCardEntityKind>(["plan", "material", "essay"]);

export function trackRichCardEntityPending(kind: string, id: string): void {
	if (!ENTITY_KINDS.has(kind as RichCardEntityKind) || !id) return;
	pendingById.set(id, { kind: kind as RichCardEntityKind, id });
}

export function clearRichCardEntityPending(id: string): void {
	pendingById.delete(id);
}

export function seedPendingFromRichCards(
	cards: ReadonlyArray<{ status: string; kind: string; id: string }>,
): void {
	for (const card of cards) {
		if (card.status !== "pending") continue;
		if (pendingById.has(card.id)) continue;
		trackRichCardEntityPending(card.kind, card.id);
	}
}

export type RichCardEntityTerminal = {
	status: "ready" | "failed";
	summary?: string;
	deepLink?: string;
	errorMessage?: string;
};

/** Читает terminal-статус entity для pending rich_card. */
export async function fetchRichCardEntityTerminal(
	pending: PendingRichCardEntity,
): Promise<RichCardEntityTerminal | null> {
	try {
		switch (pending.kind) {
			case "plan": {
				// isGenerating в UI плана: aiModel == null; failed → sentinel.
				const res = await trpcQuery<{
					plan: { aiModel: string | null };
				}>("studyPlan.get", { id: pending.id });
				if (res.plan.aiModel === "generation-failed") {
					return { status: "failed", errorMessage: "generation-failed" };
				}
				if (res.plan.aiModel != null) {
					// План == курс (обучающая система): каноничный адрес — /courses/<id>.
					return { status: "ready", deepLink: `/courses/${pending.id}` };
				}
				return null;
			}
			case "material": {
				const res = await trpcQuery<{
					processingStatus: string;
				}>("materials.get", { id: pending.id });
				if (res.processingStatus === "ready") {
					return { status: "ready", deepLink: `/materials/${pending.id}` };
				}
				if (res.processingStatus === "failed" || res.processingStatus === "cancelled") {
					return { status: "failed" };
				}
				return null;
			}
			case "essay": {
				// Статусы — `essaySubmissionStatusSchema` (draft/queued/grading/ready/failed),
				// балл — в `grading`, а не в `essay`.
				const res = await trpcQuery<{
					essay: { status: string; errorMessage?: string | null };
					grading: { finalScore: number } | null;
				}>("essays.submissions.getById", { essayId: pending.id });
				if (res.essay.status === "ready") {
					return {
						status: "ready",
						...(typeof res.grading?.finalScore === "number"
							? { summary: String(res.grading.finalScore) }
							: {}),
						deepLink: `/tools/essay/${pending.id}`,
					};
				}
				if (res.essay.status === "failed") {
					return {
						status: "failed",
						...(res.essay.errorMessage ? { errorMessage: res.essay.errorMessage } : {}),
					};
				}
				return null;
			}
		}
	} catch {
		return null;
	}
}

export async function reconcileRichCardEntitiesOnce(handlers: {
	isCancelled: () => boolean;
}): Promise<void> {
	const pending = [...pendingById.values()];
	if (pending.length === 0) return;
	for (const item of pending) {
		if (handlers.isCancelled()) return;
		if (!pendingById.has(item.id)) continue;
		const terminal = await fetchRichCardEntityTerminal(item);
		if (!terminal || handlers.isCancelled()) continue;
		if (!pendingById.has(item.id)) continue;
		pendingById.delete(item.id);
		if (terminal.status === "ready") {
			applyCardLivePatch(item.id, {
				status: "ready",
				summary: terminal.summary ?? "",
				...(terminal.deepLink ? { deepLink: terminal.deepLink } : {}),
			});
		} else {
			applyCardLivePatch(item.id, {
				status: "failed",
				...(terminal.errorMessage ? { errorMessage: terminal.errorMessage } : {}),
			});
		}
	}
}

/** @internal vitest */
export function resetRichCardEntityPendingForTests(): void {
	pendingById.clear();
}

/** @internal vitest */
export function listRichCardEntityPendingForTests(): PendingRichCardEntity[] {
	return [...pendingById.values()];
}
